import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, Play, Pause } from "lucide-react";
import { HERO_VIDEO, HERO_VIDEO_ALT, HERO_POSTER } from "../mock";

export default function HeroVideo() {
  const vref = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 80);
    return () => clearTimeout(t);
  }, []);

  const toggle = () => {
    const v = vref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };

  return (
    <section className="relative h-[100svh] min-h-[680px] w-full overflow-hidden bg-ink text-bone -mt-[78px]">
      {/* Video / poster */}
      <div className="absolute inset-0">
        <img src={HERO_POSTER} alt=""
          className={`absolute inset-0 w-full h-full object-cover kb transition-opacity duration-700 ${loaded ? "opacity-0" : "opacity-100"}`} />
        <video
          ref={vref}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${loaded ? "opacity-100" : "opacity-0"}`}
          autoPlay muted loop playsInline preload="auto"
          poster={HERO_POSTER}
          onLoadedData={() => setLoaded(true)}
          onError={() => setLoaded(false)}
        >
          <source src={HERO_VIDEO} type="video/mp4" />
          <source src={HERO_VIDEO_ALT} type="video/mp4" />
        </video>
        {/* STRONGER overlays for nav legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/15 to-black/35" />
        {/* Top vignette specifically for header */}
        <div className="absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-black/85 to-transparent" />
      </div>

      {/* Floating frame */}
      <div className="absolute inset-6 lg:inset-10 border border-bone/15 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 h-full max-w-[1440px] mx-auto px-6 lg:px-10 pt-28 pb-10 flex flex-col justify-between">
        <div className={`flex items-start justify-between transition-all duration-1000 ${entered ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"}`}>
          <div className="text-eyebrow text-bone/85 leading-[1.8]">
            <div className="inline-block mr-5 text-amber">— 001</div>
            <div className="inline-block">RS Construction (HI) Ltd · UK</div>
          </div>
          <div className="text-eyebrow text-bone/70 hidden md:block">Scroll to begin ↓</div>
        </div>

        <div className="max-w-5xl">
          <div className="overflow-hidden">
            <h1 className={`font-display-tight text-[15vw] md:text-[12vw] lg:text-[10rem] xl:text-[12rem] leading-[0.85] text-bone text-balance transition-transform duration-1000 ${entered ? "translate-y-0" : "translate-y-[120%]"}`}>
              Bespoke
            </h1>
          </div>
          <div className="overflow-hidden mt-1">
            <h1 className={`font-display italic text-[15vw] md:text-[12vw] lg:text-[10rem] xl:text-[12rem] leading-[0.85] text-amber text-balance transition-transform duration-1000 delay-150 ${entered ? "translate-y-0" : "translate-y-[120%]"}`}>
              construction.
            </h1>
          </div>

          <div className={`mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8 transition-all duration-1000 delay-500 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <p className="max-w-xl text-bone/90 text-lg md:text-xl leading-relaxed">
              From project planning to completion — comprehensive construction solutions delivered with precision, reliability and professional expertise. Your trusted partner for every build.
            </p>
            <div className="flex items-center gap-3">
              <Link to="/services" className="btn-magnetic bg-amber text-ink px-7 py-4 text-[11px] tracking-[0.25em] uppercase font-bold hover:bg-bone">
                Our Services
              </Link>
              <Link to="/contact" className="btn-magnetic border border-bone/70 text-bone px-7 py-4 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-bone hover:text-ink hover:border-bone">
                Start a Project
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Video controls */}
      <button onClick={toggle} aria-label={playing ? "Pause video" : "Play video"}
        className="absolute bottom-10 right-10 z-20 h-11 w-11 rounded-full border border-bone/40 bg-black/30 backdrop-blur flex items-center justify-center text-bone hover:bg-bone hover:text-ink transition-colors">
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-bone/70 text-[10px] tracking-[0.35em] uppercase flex flex-col items-center gap-2 float-y z-10">
        <ArrowDown className="w-3.5 h-3.5" />
        Scroll
      </div>
    </section>
  );
}
